import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { apiTokenService, TokenExpiry, ApiToken } from '../services/authApi';
import { Page, PageHeader, ConsoleTabs, SectionHeader, Notice, TableCard } from '../components/console/ui';
import { CopyIc, TrashIc } from '../components/console/icons';

// Surface the backend's own error message (e.g. "Maximum of 5 API tokens
// allowed. Please revoke an existing token." on 402) instead of a generic one.
function serverMsg(e: any, fallback: string): string {
  return e?.response?.data?.message || e?.response?.data?.error || fallback;
}

const EXPIRY_OPTIONS: { label: string; value: TokenExpiry }[] = [
  { label: 'Never', value: 'never' },
  { label: '7 days', value: '7d' },
  { label: '30 days', value: '30d' },
  { label: '90 days', value: '90d' },
  { label: '1 year', value: '365d' },
];

export default function ApiKeys() {
  const qc = useQueryClient();
  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState('');
  const [newExpiry, setNewExpiry] = useState<TokenExpiry>('never');
  const [revealed, setRevealed] = useState<string | null>(null);

  const tokensQ = useQuery({ queryKey: ['api-tokens'], queryFn: () => apiTokenService.list().then((r) => r.data) });
  const tokens: ApiToken[] = tokensQ.data?.data ?? [];

  const createM = useMutation({
    mutationFn: () => apiTokenService.create(newName.trim(), newExpiry).then((r) => r.data),
    onSuccess: (data) => {
      setRevealed(data.token ?? '');
      setNewName('');
      setNewExpiry('never');
      setShowCreate(false);
      qc.invalidateQueries({ queryKey: ['api-tokens'] });
    },
    onError: (e: any) => toast.error(serverMsg(e, 'Could not create key')),
  });

  const revokeM = useMutation({
    mutationFn: (id: string) => apiTokenService.revoke(id),
    onSuccess: () => {
      toast.success('Key revoked');
      qc.invalidateQueries({ queryKey: ['api-tokens'] });
    },
    onError: (e: any) => toast.error(serverMsg(e, 'Could not revoke key')),
  });

  const copy = (v: string) => {
    navigator.clipboard.writeText(v);
    toast.success('Copied');
  };

  const revoke = (token: ApiToken) => {
    if (window.confirm(`Revoke "${token.name}"? The CLI using this key will stop working immediately.`)) {
      revokeM.mutate(token.id);
    }
  };

  const revokeBtn = (token: ApiToken) => (
    <button
      type="button"
      className="ps-btn-danger ps-btn-sm !px-2.5"
      onClick={() => revoke(token)}
      aria-label={`Revoke ${token.name}`}
      title="Revoke"
    >
      <TrashIc className="h-4 w-4" />
    </button>
  );

  return (
    <Page>
      <PageHeader
        title="Keys & SSH"
        subtitle="An API key signs the podstack CLI in to the Podstack Training Cloud."
        actions={
          <button type="button" className="ps-btn" onClick={() => setShowCreate(true)}>
            Create API key
          </button>
        }
      />
      <ConsoleTabs />

      {revealed !== null && (
        <div className="ps-card flex flex-col gap-3 border-ps-accent-line p-5" role="status">
          <p className="m-0 text-sm font-medium text-primary-300">Copy your key now - it won't be shown again.</p>
          <div className="ps-copy">
            <code>{revealed}</code>
            <button type="button" className="ps-cb gap-1.5" onClick={() => copy(revealed)} aria-label="Copy key">
              <CopyIc className="h-3.5 w-3.5" /> Copy
            </button>
          </div>
          <button type="button" className="self-start text-xs text-ps-subtle hover:text-ps-text" onClick={() => setRevealed(null)}>
            Dismiss
          </button>
        </div>
      )}

      <section className="flex flex-col gap-3" aria-labelledby="keys-api">
        <SectionHeader id="keys-api" title="API keys for the CLI" />

        {showCreate && (
          <div className="ps-card flex flex-col gap-3.5 p-5">
            <div className="grid gap-3.5 [grid-template-columns:repeat(auto-fit,minmax(min(100%,220px),1fr))]">
              <label className="flex flex-col gap-1.5 text-[13px]">
                <span className="text-xs text-ps-muted">Key name</span>
                <input
                  className="ps-input"
                  placeholder="Key name (e.g. laptop)"
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                />
              </label>
              <label className="flex flex-col gap-1.5 text-[13px]">
                <span className="text-xs text-ps-muted">Expires</span>
                <select className="ps-input" value={newExpiry} onChange={(e) => setNewExpiry(e.target.value as TokenExpiry)}>
                  {EXPIRY_OPTIONS.map((o) => (
                    <option key={o.label} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                className="ps-btn"
                disabled={!newName.trim() || createM.isPending}
                onClick={() => createM.mutate()}
              >
                {createM.isPending ? 'Creating…' : 'Create'}
              </button>
              <button type="button" className="ps-btn2" onClick={() => setShowCreate(false)}>
                Cancel
              </button>
            </div>
          </div>
        )}

        {tokensQ.isLoading ? (
          <Notice>Loading keys…</Notice>
        ) : tokensQ.isError ? (
          <Notice tone="error">Could not load API keys.</Notice>
        ) : tokens.length === 0 ? (
          <div className="ps-card flex flex-wrap items-center gap-4 px-6 py-7">
            <div className="flex-[1_1_320px]">
              <div className="font-semibold text-ps-text">No API keys yet</div>
              <div className="mt-1 text-[13px] text-ps-muted">
                Create one to authenticate the CLI with <span className="font-mono">podstack auth login</span>.
              </div>
            </div>
            <button type="button" className="ps-btn" onClick={() => setShowCreate(true)}>
              Create API key
            </button>
          </div>
        ) : (
          <>
            <TableCard className="hidden md:block">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Key</th>
                  <th>Created</th>
                  <th>Expires</th>
                  <th>
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {tokens.map((token) => (
                  <tr key={token.id}>
                    <td className="font-medium text-ps-text">{token.name}</td>
                    <td className="font-mono text-ps-muted">{token.prefix}…</td>
                    <td className="text-ps-muted">{new Date(token.created_at).toLocaleDateString()}</td>
                    <td className="text-ps-muted">
                      {token.expires_at ? new Date(token.expires_at).toLocaleDateString() : 'Never'}
                    </td>
                    <td className="text-right">{revokeBtn(token)}</td>
                  </tr>
                ))}
              </tbody>
            </TableCard>

            <div className="flex flex-col gap-3 md:hidden">
              {tokens.map((token) => (
                <div key={token.id} className="ps-card flex flex-col gap-3 p-4">
                  <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <div className="font-medium text-ps-text">{token.name}</div>
                      <code className="font-mono text-xs text-ps-subtle">{token.prefix}…</code>
                    </div>
                    {revokeBtn(token)}
                  </div>
                  <div className="flex flex-wrap gap-4 border-t border-ps-raised pt-3 text-xs text-ps-subtle">
                    <span>Created {new Date(token.created_at).toLocaleDateString()}</span>
                    {token.expires_at && <span>Expires {new Date(token.expires_at).toLocaleDateString()}</span>}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </section>

      <section className="flex flex-col gap-3" aria-labelledby="keys-ssh">
        <SectionHeader id="keys-ssh" title="SSH keys" />
        <div className="ps-card flex flex-wrap items-center gap-3 px-[18px] py-3.5 text-[13px]">
          <span className="flex-[1_1_320px] text-ps-muted">
            SSH keys let the CLI open sessions on your instances. Pick an existing key or paste a new public key when
            you create an instance.
          </span>
          <Link to="/console/instances" className="ps-btn2 ps-btn-sm">
            Go to Instances
          </Link>
        </div>
      </section>
    </Page>
  );
}
